import * as React from "react";
import { flushSync } from "react-dom";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { DefaultButton } from "@fluentui/react/lib/Button";
import { Text } from "@fluentui/react";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Email, LTLOffer, LTLShipment } from "@src/types/api";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { MessageBar, MessageBarType } from "@fluentui/react/lib/MessageBar";
import { useProviderConfig } from "@taskpane/hooks/useProviderConfig";
import { useLTLReducer, type LTLState } from "@taskpane/hooks/useLTLReducer";
import { useLTLPricing, extractOffersFromEmail } from "@taskpane/hooks/useLTLPricing";
import { normalizeClickedShipment } from "@taskpane/utils/normalizeLTL";
import { ITextFieldStyles } from "@fluentui/react/lib/TextField";
import { validateLTL } from "@taskpane/utils/validateLTL";
import {
  focusLtlValidationError,
  getFirstLtlValidationErrorKey,
  getLtlDetailsTabForError,
  summarizeLtlValidationErrors,
} from "@taskpane/utils/ltlValidationFocus";
import { ShipmentDetailsForm } from "./ShipmentDetailsForm";
import { BolDetailsForm } from "./BolDetailsForm";
import { ItemsEditor } from "./ItemsEditor";
import { AccessorialsPicker } from "./AccessorialsPicker";
import { OffersGrid } from "./OffersGrid";
import { useTaskpaneServices } from "@src/load-agent/app/TaskpaneServicesContext";
import ConfirmDialog from "@taskpane/components/ConfirmDialog";
import FeedbackDialog from "@taskpane/components/FeedbackDialog";
import { getLatestFeedbackValue } from "@taskpane/utils/feedbackUtils";
import { formatTmsOffersLoadingLabel } from "@taskpane/utils/tmsOffersLoadingLabel";
import { applyPreparedBol } from "@taskpane/utils/applyPreparedBol";
import type { InferredFormFields } from "@taskpane/utils/computeInferredFormFields";
import { inferFieldsForLtlLoad } from "@taskpane/utils/computeInferredFormFields";
import type { ShipmentDraft } from "@src/load-agent/domain/shipmentDraft";
import { styles } from "./LTL.styles";
import { emailShipmentsLtl } from "@taskpane/utils/emailShipments";
import { sellPriceFromMarginPercent } from "@shared/utils/marginOnSell";

const LTL_ORIGINAL_STORAGE_KEY_PREFIX = "autobidder_ltl_original_";

function roundLtlMargin(n: number): number {
  return Math.round(n * 100) / 100;
}

function hydrateLtlMarginState(
  offers: LTLOffer[],
  defaultMarginPercent = 0
): {
  marginPct: Record<string, number>;
  marginModes: Record<string, "percent" | "dollar">;
  marginDollars: Record<string, number>;
} {
  const marginPct: Record<string, number> = {};
  const marginModes: Record<string, "percent" | "dollar"> = {};
  const marginDollars: Record<string, number> = {};

  for (const offer of offers) {
    const id = offer.id;
    const price = Number(offer.price) || 0;
    const rawPct = Number(
      offer.appliedMarginPercent ?? offer.marginPct ?? defaultMarginPercent ?? 0
    );
    const mode = offer.marginInputMode === "dollar" ? "dollar" : "percent";

    marginModes[id] = mode;
    marginPct[id] = roundLtlMargin(rawPct);
    if (
      mode === "dollar" &&
      typeof offer.marginDollars === "number" &&
      Number.isFinite(offer.marginDollars)
    ) {
      marginDollars[id] = roundLtlMargin(offer.marginDollars);
    } else {
      const sell = sellPriceFromMarginPercent(price, rawPct);

      marginDollars[id] = roundLtlMargin(Math.max(0, sell - price));
    }
  }
  return { marginPct, marginModes, marginDollars };
}

function defaultLtlMarginPercent(emailData: Email | null): number {
  const standard = emailData?.organization?.marginDecision?.ltl?.standard;

  if (typeof standard === "number" && Number.isFinite(standard)) {
    return Math.round(standard * 10000) / 100;
  }
  return 0;
}

type LtlDraftPayload = {
  form: ReturnType<typeof useLTLReducer>["state"]["form"];
  accessorials: ReturnType<typeof useLTLReducer>["state"]["accessorials"];
  items: ReturnType<typeof useLTLReducer>["state"]["items"];
  shipmentMode: ReturnType<typeof useLTLReducer>["state"]["shipmentMode"];
  equipment: ReturnType<typeof useLTLReducer>["state"]["equipment"];
};

function buildLtlDraftPayload(
  state: ReturnType<typeof useLTLReducer>["state"],
  overrides: Partial<LtlDraftPayload> = {}
): LtlDraftPayload {
  return {
    form: state.form,
    accessorials: state.accessorials,
    items: state.items,
    shipmentMode: state.shipmentMode,
    equipment: state.equipment,
    ...overrides,
  };
}

function serializeLtlDraftPayload(payload: LtlDraftPayload): string {
  return JSON.stringify(payload);
}

function ltlStateForValidation(
  draft: ShipmentDraft,
  inferredFormFields: InferredFormFields
): LTLState {
  return {
    form: draft.form,
    items: draft.items,
    shipmentMode: draft.shipmentMode,
    equipment: draft.equipment,
    accessorials: draft.accessorials,
    errors: {},
    inferredFormFields,
  };
}

interface Props {
  setActiveComponent: (name: string) => void;
  emailData: Email | null;
  setEmailData: (emailData: Email | null) => void;
  clickedLTLShipment: LTLShipment | null;
  setClickedLTLShipment: (shipment: LTLShipment | null) => void;
  selectedMode: string | null;
  setSelectedMode: (mode: "LTL" | "FTL" | null) => void;
}

export default function LTL({
  setActiveComponent,
  emailData,
  setEmailData,
  clickedLTLShipment,
  setClickedLTLShipment,
  setSelectedMode,
}: Props) {
  const providerConfig = useProviderConfig(emailData);
  const providerId = providerConfig.id;
  const offersLoadingLabel = useMemo(
    () => formatTmsOffersLoadingLabel(providerConfig.displayName, emailData),
    [providerConfig.displayName, emailData]
  );
  const { loadApp } = useTaskpaneServices();
  const {
    state,
    loadFromShipment,
    setField,
    setShipmentMode,
    setEquipment,
    setItemField,
    addItem,
    removeItem,
    toggleAccessorial,
    setErrors,
    reset,
  } = useLTLReducer(providerConfig.accessorials);
  const {
    price,
    selectOffer,
    loading,
    error: pricingError,
  } = useLTLPricing({
    emailData,
    clickedLTLShipment,
    setEmailData,
    setClickedLTLShipment,
  });
  const [offerData, setOfferData] = useState<LTLOffer[]>([]);
  const [selectedOffers, setSelectedOffers] = useState<string[]>([]);
  const [offerMargins, setOfferMargins] = useState<Record<string, number>>({});
  const [offerMarginModes, setOfferMarginModes] = useState<Record<string, "percent" | "dollar">>(
    {}
  );
  const [offerMarginDollars, setOfferMarginDollars] = useState<Record<string, number>>({});
  const [resetError, setResetError] = useState<string | null>(null);
  const [hydrateError, setHydrateError] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [detailsSectionTab, setDetailsSectionTab] = useState<"shipment" | "bol">("shipment");
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [submitValidationMessage, setSubmitValidationMessage] = useState<string | null>(null);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);
  const validationMessageRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const lastSavedDraftRef = useRef<string>("");
  const stateRef = useRef(state);
  const textFieldStyles = useCallback(
    (hasError: boolean, inferred: boolean): Partial<ITextFieldStyles> => ({
      fieldGroup: {
        borderRadius: "5px",
        borderColor: hasError ? "#d13438" : "#eff1f3",
        borderWidth: "2px",
        backgroundColor: inferred && !hasError ? "#fff8e6" : "#ffffff",
        selectors: {
          "&:hover": {
            borderColor: hasError ? "#d13438" : "#c7c7c7",
          },
          "&:focus, &.is-active": {
            borderColor: hasError ? "#d13438" : "#0078d4",
          },
          "&& .ms-Label": {
            fontSize: "12px",
            fontWeight: 500,
            color: "#605e5c",
            marginBottom: "4px",
          },
        },
      },
      field: {
        fontSize: "13px",
        lineHeight: "18px",
        fontWeight: 400,
        color: "#323130",
      },
    }),
    []
  );
  const getTextFieldStyles = useCallback(
    (fieldName: string) =>
      textFieldStyles(!!state.errors[fieldName], !!state.inferredFormFields[fieldName]),
    [state.errors, state.inferredFormFields, textFieldStyles]
  );
  const overallError = hydrateError ?? resetError ?? pricingError;

  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  useEffect(() => {
    if (errorRef.current && overallError) {
      errorRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [overallError]);
  useEffect(() => {
    if (Object.keys(state.errors).length === 0) {
      setSubmitValidationMessage(null);
    }
  }, [state.errors]);
  useEffect(() => {
    if (submitValidationMessage && validationMessageRef.current) {
      validationMessageRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [submitValidationMessage]);
  useEffect(() => {
    const shipmentId = clickedLTLShipment?.shipmentId;

    if (!shipmentId) return;
    setHydrateError(null);
    try {
      const normalized = normalizeClickedShipment(clickedLTLShipment, {
        providerId,
        accessorialDefaults: providerConfig.accessorials,
      });
      const rf = clickedLTLShipment.requiredFields;

      if (rf) {
        try {
          const key = `${LTL_ORIGINAL_STORAGE_KEY_PREFIX}${shipmentId}`;

          window.localStorage.setItem(key, JSON.stringify(rf));
        } catch {
          // ignore local storage write failures
        }
      }
      const merged = applyPreparedBol(normalized, clickedLTLShipment.preparedBol);
      const withBol = { ...normalized, form: merged.form, items: merged.items };
      const inferredFormFields = inferFieldsForLtlLoad(clickedLTLShipment.normalizedDraft);

      loadFromShipment({ ...withBol, inferredFormFields });
      setErrors(validateLTL(ltlStateForValidation(withBol, inferredFormFields), providerConfig));
      lastSavedDraftRef.current = serializeLtlDraftPayload({
        form: withBol.form,
        accessorials: withBol.accessorials,
        items: withBol.items,
        shipmentMode: withBol.shipmentMode,
        equipment: withBol.equipment,
      });
      setOfferData(normalized.offers);
      setSelectedOffers(normalized.selectedOffers ?? []);
      const hydrated = hydrateLtlMarginState(
        normalized.offers ?? [],
        defaultLtlMarginPercent(emailData)
      );

      setOfferMargins(hydrated.marginPct);
      setOfferMarginModes(hydrated.marginModes);
      setOfferMarginDollars(hydrated.marginDollars);
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : "Could not load LTL form from this shipment (missing normalizedDraft or bad data).";

      setHydrateError(msg);
    }
  }, [
    clickedLTLShipment?.shipmentId,
    clickedLTLShipment?.quotedRates,
    clickedLTLShipment?.requiredFields,
    clickedLTLShipment?.preparedBol,
    clickedLTLShipment?.normalizedDraft,
    providerId,
    providerConfig.accessorials,
    providerConfig,
    loadFromShipment,
    setErrors,
  ]);
  const navBack = useCallback(() => {
    setSelectedMode(null);
    setActiveComponent("Shipments");
  }, [setSelectedMode, setActiveComponent]);
  const isFormValidForPricing = useMemo(() => {
    const errors = validateLTL(state, providerConfig, { includeBolDetails: false });

    return Object.keys(errors).length === 0;
  }, [state, providerConfig]);

  const staleRatesLabel = useMemo(() => {
    const { quotedAt, quotedRates } = clickedLTLShipment ?? {};

    if (!quotedAt || !quotedRates?.length) return null;
    const quotedDate = new Date(quotedAt);
    const today = new Date();
    const isStale =
      quotedDate.getFullYear() !== today.getFullYear() ||
      quotedDate.getMonth() !== today.getMonth() ||
      quotedDate.getDate() !== today.getDate();

    if (!isStale) return null;
    const label = quotedDate.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

    return `Offer is from ${label}. May need to refresh rates!`;
  }, [clickedLTLShipment]);
  const handleSubmitPricing = useCallback(async () => {
    const errors = validateLTL(state, providerConfig, { includeBolDetails: false });
    const errorKeys = Object.keys(errors);

    if (errorKeys.length > 0) {
      const firstKey = getFirstLtlValidationErrorKey(errors)!;
      const tab = getLtlDetailsTabForError(firstKey, {
        requiresNmfcPerItem: !!providerConfig.ltlFormCapabilities?.requiresNmfcPerItem,
      });
      const summary = summarizeLtlValidationErrors(errors);

      flushSync(() => {
        setDetailsSectionTab(tab);
        setErrors(errors);
        setSubmitValidationMessage(summary);
      });
      queueMicrotask(() => {
        focusLtlValidationError(firstKey);
        window.setTimeout(() => focusLtlValidationError(firstKey), 120);
      });
      return;
    }
    setSubmitValidationMessage(null);
    setSelectedOffers([]);
    const result = await price({ state, loaderRef });

    if (result.success && result.refreshedEmail && result.shipmentId) {
      const refreshedOffers = extractOffersFromEmail(result.refreshedEmail, result.shipmentId);

      setOfferData(refreshedOffers);
      const hydratedOffers = hydrateLtlMarginState(
        refreshedOffers,
        defaultLtlMarginPercent(result.refreshedEmail)
      );

      setOfferMargins(hydratedOffers.marginPct);
      setOfferMarginModes(hydratedOffers.marginModes);
      setOfferMarginDollars(hydratedOffers.marginDollars);
    }
  }, [
    state,
    providerConfig,
    price,
    emailData,
    clickedLTLShipment?.shipmentId,
    setErrors,
    setDetailsSectionTab,
  ]);
  const handleSelectOffers = useCallback(async () => {
    const shipmentId = clickedLTLShipment?.shipmentId;

    if (!shipmentId || selectedOffers.length === 0 || !offerData.length) return;
    const margins: Record<string, number> = {};
    const ltlMarginModes: Record<string, "percent" | "dollar"> = {};
    const ltlMarginDollars: Record<string, number> = {};

    for (const offer of offerData) {
      const id = offer.id;
      const mode = offerMarginModes[id] ?? "percent";
      const pct = offerMargins[id] ?? 0;
      const dollars = offerMarginDollars[id] ?? 0;
      const price = Number(offer.price) || 0;

      margins[id] =
        mode === "percent"
          ? roundLtlMargin(pct)
          : price > 0
            ? roundLtlMargin((dollars / price) * 100)
            : 0;
      ltlMarginModes[id] = mode;
      if (mode === "dollar") {
        ltlMarginDollars[id] = roundLtlMargin(dollars);
      }
    }
    const success = await selectOffer({
      shipmentId,
      selectedOfferIds: selectedOffers,
      margins,
      ltlMarginModes,
      ltlMarginDollars,
    });

    if (success) navBack();
  }, [
    clickedLTLShipment,
    selectedOffers,
    offerData,
    offerMargins,
    offerMarginModes,
    offerMarginDollars,
    selectOffer,
    navBack,
  ]);
  const handleToggleOffer = useCallback((offerId: string) => {
    setSelectedOffers((prev) =>
      prev.includes(offerId) ? prev.filter((id) => id !== offerId) : [...prev, offerId]
    );
  }, []);
  const handleChangeOfferMarginPct = useCallback((offerId: string, marginPct: number) => {
    setOfferMargins((prev) => ({
      ...prev,
      [offerId]: Math.round(marginPct * 100) / 100,
    }));
  }, []);
  const handleChangeOfferMarginDollars = useCallback((offerId: string, marginDollars: number) => {
    setOfferMarginDollars((prev) => ({
      ...prev,
      [offerId]: Math.round(marginDollars * 100) / 100,
    }));
  }, []);
  const handleChangeOfferMarginMode = useCallback(
    (offerId: string, mode: "percent" | "dollar") => {
      const prev = offerMarginModes[offerId] ?? "percent";

      if (prev === mode) return;
      const price = Number(offerData.find((o) => o.id === offerId)?.price) || 0;

      setOfferMarginModes((m) => ({ ...m, [offerId]: mode }));
      if (mode === "dollar") {
        const pct = offerMargins[offerId] ?? 0;
        const sell = sellPriceFromMarginPercent(price, pct);

        setOfferMarginDollars((d) => ({
          ...d,
          [offerId]: roundLtlMargin(Math.max(0, sell - price)),
        }));
      } else {
        const dollars = offerMarginDollars[offerId] ?? 0;
        const sell = price + dollars;

        setOfferMargins((p) => ({
          ...p,
          [offerId]: sell > 0 ? roundLtlMargin(((sell - price) / sell) * 100) : 0,
        }));
      }
    },
    [offerMarginModes, offerData, offerMargins, offerMarginDollars]
  );
  const handleNextClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedOffers.length === 0) return;
    void handleSelectOffers();
  };
  const clearForm = useCallback(() => {
    reset();
  }, [reset]);
  const persistDraft = useCallback(
    async (payloadOverride?: LtlDraftPayload) => {
      const shipmentId = clickedLTLShipment?.shipmentId;
      const internetMessageId = emailData?.internetMessageId;

      if (!shipmentId || !internetMessageId) {
        return;
      }
      const payload = payloadOverride ?? buildLtlDraftPayload(stateRef.current);
      const snapshot = serializeLtlDraftPayload(payload);

      if (snapshot === lastSavedDraftRef.current) {
        return;
      }
      try {
        await loadApp.saveLTLPricing({
          shipmentId,
          internetMessageId,
          formData: payload.form,
          existingShipment: clickedLTLShipment,
          accessorials: payload.accessorials,
          items: payload.items,
          shipmentMode: payload.shipmentMode,
          equipment: payload.equipment,
          selectedMode: "LTL",
        });
        lastSavedDraftRef.current = snapshot;
        const refreshed = await loadApp.getEmails(internetMessageId);

        if (refreshed) {
          setEmailData(refreshed);
          const updated = emailShipmentsLtl(refreshed).find((s) => s.shipmentId === shipmentId);

          if (updated) setClickedLTLShipment(updated);
        }
      } catch (err) {
        setResetError(err instanceof Error ? err.message : "Failed to save shipment details");
      }
    },
    [clickedLTLShipment, emailData?.internetMessageId, loadApp, setEmailData, setClickedLTLShipment]
  );
  const resetToOriginal = useCallback(async () => {
    setShowResetConfirm(false);
    setResetError(null);
    const shipmentId = clickedLTLShipment?.shipmentId;
    const internetMessageId = emailData?.internetMessageId;

    if (!shipmentId || !internetMessageId) return;
    try {
      const key = `${LTL_ORIGINAL_STORAGE_KEY_PREFIX}${shipmentId}`;
      const stored = window.localStorage.getItem(key);

      if (!stored) {
        setResetError("No original state found for this shipment.");
        return;
      }
      const requiredFields = JSON.parse(stored) as Record<string, unknown>;

      await loadApp.resetLTLRequiredFields({
        shipmentId,
        internetMessageId,
        requiredFields,
      });
      const refreshed = await loadApp.getEmails(internetMessageId);

      if (!refreshed) return;
      setEmailData(refreshed);
      const shipment = emailShipmentsLtl(refreshed).find((s) => s.shipmentId === shipmentId);

      if (shipment) {
        const normalized = normalizeClickedShipment(shipment, {
          providerId,
          accessorialDefaults: providerConfig.accessorials,
        });
        const merged = applyPreparedBol(normalized, shipment.preparedBol);
        const withBol = { ...normalized, form: merged.form, items: merged.items };
        const inferredFormFields = inferFieldsForLtlLoad(shipment.normalizedDraft);

        loadFromShipment({ ...withBol, inferredFormFields });
        setErrors(validateLTL(ltlStateForValidation(withBol, inferredFormFields), providerConfig));
        lastSavedDraftRef.current = serializeLtlDraftPayload({
          form: withBol.form,
          accessorials: withBol.accessorials,
          items: withBol.items,
          shipmentMode: withBol.shipmentMode,
          equipment: withBol.equipment,
        });
        setOfferData(normalized.offers);
        setSelectedOffers(normalized.selectedOffers ?? []);
        const hydratedReset = hydrateLtlMarginState(
          normalized.offers ?? [],
          defaultLtlMarginPercent(refreshed)
        );

        setOfferMargins(hydratedReset.marginPct);
        setOfferMarginModes(hydratedReset.marginModes);
        setOfferMarginDollars(hydratedReset.marginDollars);
      }
    } catch (err) {
      setResetError(err instanceof Error ? err.message : "An unexpected error occurred");
    }
  }, [
    clickedLTLShipment?.shipmentId,
    emailData?.internetMessageId,
    loadApp,
    setEmailData,
    providerId,
    providerConfig.accessorials,
    providerConfig,
    loadFromShipment,
    lastSavedDraftRef,
    setErrors,
  ]);
  const applyFormValidation = useCallback(() => {
    setErrors(validateLTL(state, providerConfig));
  }, [setErrors, state, providerConfig]);
  const handleDraftBlur = useCallback(() => {
    window.setTimeout(() => {
      applyFormValidation();
      void persistDraft();
    }, 0);
  }, [applyFormValidation, persistDraft]);
  const focusShipmentDetailsTab = useCallback(() => {
    setDetailsSectionTab("shipment");
    applyFormValidation();
  }, [applyFormValidation]);
  const focusBolDetailsTab = useCallback(() => {
    setDetailsSectionTab("bol");
    applyFormValidation();
  }, [applyFormValidation]);
  const handleShipmentModeChange = useCallback(
    (value: "LTL" | "Truckload") => {
      setShipmentMode(value);
      void persistDraft(buildLtlDraftPayload(state, { shipmentMode: value }));
    },
    [setShipmentMode, persistDraft, state]
  );
  const handleEquipmentChange = useCallback(
    (value: string) => {
      setEquipment(value);
      void persistDraft(buildLtlDraftPayload(state, { equipment: value }));
    },
    [setEquipment, persistDraft, state]
  );
  const handleToggleAccessorial = useCallback(
    (key: string) => {
      toggleAccessorial(key);
      const nextAccessorials = state.accessorials.map((acc) =>
        acc.key === key ? { ...acc, checked: !acc.checked } : acc
      );

      void persistDraft(buildLtlDraftPayload(state, { accessorials: nextAccessorials }));
    },
    [toggleAccessorial, state, persistDraft]
  );
  const latestThumb = getLatestFeedbackValue(emailData);
  const feedbackIconName =
    typeof latestThumb === "boolean" ? (latestThumb ? "Like" : "Dislike") : undefined;
  const handleSubmitFeedback = useCallback(
    async (feedback: boolean, message: string) => {
      if (!emailData?.internetMessageId) return;
      setFeedbackSubmitting(true);
      try {
        const { feedback_entries } = await loadApp.saveEmailFeedback({
          internetMessageId: emailData.internetMessageId,
          feedback,
          feedbackContext: message,
        });

        setEmailData({
          ...emailData,
          feedback_entries,
        });
        setFeedbackDialogOpen(false);
      } catch (err) {
        setResetError(err instanceof Error ? err.message : "Failed to submit feedback");
      } finally {
        setFeedbackSubmitting(false);
      }
    },
    [emailData, loadApp, setEmailData]
  );

  return (
    <Box className="ltl-container" sx={styles.container}>
      {overallError && (
        <Box ref={errorRef} sx={styles.errorWrapper}>
          <MessageBar
            messageBarType={MessageBarType.error}
            isMultiline={false}
            onDismiss={() => {
              setResetError(null);
              setHydrateError(null);
            }}
          >
            {overallError}
          </MessageBar>
        </Box>
      )}
      {submitValidationMessage && (
        <Box ref={validationMessageRef} sx={styles.errorWrapper}>
          <MessageBar
            messageBarType={MessageBarType.warning}
            isMultiline
            onDismiss={() => setSubmitValidationMessage(null)}
          >
            {submitValidationMessage}
          </MessageBar>
        </Box>
      )}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        ref={headerRef}
        sx={styles.header}
      >
        <Stack direction="row" alignItems="center">
          <DefaultButton
            text="Back"
            onClick={navBack}
            allowDisabledFocus
            className="back-button"
            iconProps={{ iconName: "Back", styles: { root: styles.backIconRoot } }}
          />
          <Text variant="large" styles={{ root: styles.title }}>
            LTL Shipment
          </Text>
        </Stack>
        <DefaultButton
          text="Feedback"
          iconProps={feedbackIconName ? { iconName: feedbackIconName } : undefined}
          onClick={() => setFeedbackDialogOpen(true)}
          disabled={!emailData?.internetMessageId}
          styles={
            typeof latestThumb === "boolean"
              ? {
                  root: {
                    border: `1px solid ${latestThumb ? "#107C10" : "#A4262C"}`,
                    color: latestThumb ? "#107C10" : "#A4262C",
                  },
                  rootHovered: {
                    border: `1px solid ${latestThumb ? "#0B6A0B" : "#7F161D"}`,
                    color: latestThumb ? "#0B6A0B" : "#7F161D",
                  },
                  icon: {
                    color: latestThumb ? "#107C10" : "#A4262C",
                  },
                }
              : styles.secondaryButton
          }
          title="Submit feedback (you can send multiple times)"
        />
      </Stack>

      <Box sx={styles.requiredNote}>
        <Text styles={styles.requiredText}>* Required fields</Text>
      </Box>

      <form autoComplete="off">
        <Box sx={styles.section}>
          <Stack direction="row" sx={styles.sectionTabs} role="tablist">
            <Box
              component="button"
              type="button"
              role="tab"
              aria-selected={detailsSectionTab === "shipment"}
              sx={
                detailsSectionTab === "shipment"
                  ? { ...styles.sectionTab, ...styles.sectionTabActive }
                  : styles.sectionTab
              }
              onClick={focusShipmentDetailsTab}
            >
              <Text variant="medium" styles={styles.sectionTitle}>
                SHIPMENT DETAILS
              </Text>
            </Box>
            {providerConfig.ltlFormCapabilities && (
              <Box
                component="button"
                type="button"
                role="tab"
                aria-selected={detailsSectionTab === "bol"}
                sx={
                  detailsSectionTab === "bol"
                    ? { ...styles.sectionTab, ...styles.sectionTabActive }
                    : styles.sectionTab
                }
                onClick={focusBolDetailsTab}
              >
                <Text variant="medium" styles={styles.sectionTitle}>
                  BOL DETAILS
                </Text>
              </Box>
            )}
          </Stack>
          <Box sx={styles.divider} />
          {detailsSectionTab === "shipment" && (
            <ShipmentDetailsForm
              state={state}
              providerConfig={providerConfig}
              onFieldChange={setField}
              onFieldBlur={handleDraftBlur}
              onShipmentModeChange={handleShipmentModeChange}
              onEquipmentChange={handleEquipmentChange}
              getTextFieldStyles={getTextFieldStyles}
            />
          )}
          {detailsSectionTab === "bol" && providerConfig.ltlFormCapabilities && (
            <BolDetailsForm
              form={state.form}
              errors={state.errors}
              inferredFormFields={state.inferredFormFields}
              caps={providerConfig.ltlFormCapabilities}
              onFieldChange={setField}
              onFieldBlur={handleDraftBlur}
              getTextFieldStyles={getTextFieldStyles}
            />
          )}
        </Box>

        <Box sx={styles.section}>
          <Text variant="medium" styles={styles.sectionTitle}>
            FREIGHT ITEMS
          </Text>
          <Box sx={styles.divider} />
          <ItemsEditor
            state={state}
            showAddItemInTruckloadMode={providerConfig.showAddItemInTruckloadMode ?? false}
            showNmfc={
              detailsSectionTab === "bol" &&
              !!providerConfig.ltlFormCapabilities?.requiresNmfcPerItem
            }
            nmfcRequired={
              detailsSectionTab === "bol" &&
              !!providerConfig.ltlFormCapabilities?.requiresNmfcPerItem
            }
            onItemFieldChange={setItemField}
            onFieldBlur={handleDraftBlur}
            onAddItem={addItem}
            onRemoveItem={removeItem}
            getTextFieldStyles={getTextFieldStyles}
            maxWeight={emailData?.organization?.quotingLimits?.ltl?.maxWeight}
          />
        </Box>

        <Box sx={styles.section}>
          <Text variant="medium" styles={styles.sectionTitle}>
            ACCESSORIALS
          </Text>
          <Box sx={styles.divider} />
          <AccessorialsPicker
            accessorials={state.accessorials}
            onToggle={handleToggleAccessorial}
          />
        </Box>

        <Box sx={styles.actionsContainer}>
          <DefaultButton
            text="View Offers"
            type="button"
            onClick={handleSubmitPricing}
            allowDisabledFocus
            styles={styles.primaryButton}
            disabled={loading}
            title={
              !isFormValidForPricing
                ? "Please fill in all required fields (origin, destination, pickup date, class, weight, and dimensions for each item)"
                : ""
            }
          />
          {staleRatesLabel && (
            <div style={{ fontSize: "11px", color: "#d32f2f", marginTop: "4px" }}>
              {staleRatesLabel}
            </div>
          )}
          <Stack direction="row" sx={styles.secondaryActions} useFlexGap flexWrap="wrap">
            <DefaultButton
              text="Next"
              type="button"
              title="Select one or more offers"
              onClick={handleNextClick}
              styles={styles.secondaryButton}
              disabled={selectedOffers.length === 0}
            />
            <DefaultButton
              text="Reset"
              type="button"
              title="Restore shipment to original state and save to server"
              onClick={() => setShowResetConfirm(true)}
              allowDisabledFocus
              styles={styles.tertiaryButton}
            />
            <DefaultButton
              text="Clear"
              type="button"
              title="Clear all inputs (does not save)"
              onClick={clearForm}
              allowDisabledFocus
              styles={styles.tertiaryButton}
            />
          </Stack>
        </Box>
      </form>
      <ConfirmDialog
        open={showResetConfirm}
        title="Reset shipment?"
        message="This will restore the shipment to its original state and save it to the server."
        confirmText="Yes"
        cancelText="No"
        onConfirm={() => void resetToOriginal()}
        onCancel={() => setShowResetConfirm(false)}
      />
      <FeedbackDialog
        open={feedbackDialogOpen}
        initialFeedback={null}
        initialMessage=""
        submitting={feedbackSubmitting}
        onSubmit={(fb, msg) => handleSubmitFeedback(fb, msg)}
        onCancel={() => {
          if (!feedbackSubmitting) setFeedbackDialogOpen(false);
        }}
      />
      {loading && (
        <Box className="offers-loading-container" ref={loaderRef} sx={styles.loadingWrapper}>
          <Spinner size={SpinnerSize.large} label={offersLoadingLabel} />
        </Box>
      )}
      {!loading && offerData.length > 0 && (
        <OffersGrid
          offers={offerData}
          selectedOffers={selectedOffers}
          onToggleOffer={handleToggleOffer}
          onChangeMarginPct={handleChangeOfferMarginPct}
          onChangeMarginDollars={handleChangeOfferMarginDollars}
          onChangeMarginMode={handleChangeOfferMarginMode}
          offerMargins={offerMargins}
          offerMarginModes={offerMarginModes}
          offerMarginDollars={offerMarginDollars}
        />
      )}
    </Box>
  );
}
